// src/App.jsx - Light-only UI with left sidebar navigation
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, NavLink, useLocation } from "react-router-dom";
import { LogOut, BarChart3, UploadCloud } from "lucide-react";
import { isAuthenticated, logout } from "./api";
import FolderUploader from "./components/FolderUploader";
import DataExtractor from "./components/DataExtractor";
import ExtractedData from "./components/ExtractedData";
import Dashboard from "./components/Dashboard";
import AuthSectionThree from "./components/ui/auth-section-3";
import Logo from "./components/ui/Logo";
import { ThemeProvider } from "./contexts/ThemeContext";
import "./App.css";

function ProtectedRoute({ children, onAuthChange }) {
  const [authChecked, setAuthChecked] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const authStatus = isAuthenticated();
    setAuthenticated(authStatus);
    setAuthChecked(true);
    if (typeof onAuthChange === 'function') {
      onAuthChange(authStatus);
    }
  }, []);

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#eef2f4] flex items-center justify-center">
        <p className="text-sm text-gray-500">Checking authentication…</p>
      </div>
    );
  }

  return authenticated ? children : <Navigate to="/login" replace />;
}

function currentUserEmail() {
  const token = localStorage.getItem('access_token');
  if (!token) return '';
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || '';
  } catch {
    return '';
  }
}

function Sidebar({ onAuthChange }) {
  const email = currentUserEmail();

  const handleLogout = () => {
    logout();
    onAuthChange(false);
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? 'bg-[#1B5F6E]/10 text-[#1B5F6E]'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  return (
    <aside className="fixed inset-y-0 left-0 z-20 flex w-60 flex-col bg-white border-r border-[#1B5F6E]/10">
      <div className="flex h-16 items-center gap-2.5 border-b border-[#1B5F6E]/10 px-5">
        <Logo height={26} />
        <span className="text-sm font-bold text-gray-900">Verwendungsnachweis</span>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        <NavLink to="/dashboard" className={linkClass}>
          <BarChart3 className="h-4 w-4" />
          Dashboard
        </NavLink>
        <NavLink to="/" end className={linkClass}>
          <UploadCloud className="h-4 w-4" />
          Upload PDFs
        </NavLink>
      </nav>

      <div className="border-t border-[#1B5F6E]/10 px-5 py-4">
        <p className="truncate text-xs text-gray-500" title={email}>{email}</p>
        <button
          onClick={handleLogout}
          className="mt-2 flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-red-600"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      </div>
    </aside>
  );
}

function AppContent() {
  const [authenticated, setAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setAuthenticated(isAuthenticated());
  }, [location]);

  const handleAuthChange = (authStatus) => {
    setAuthenticated(authStatus);
  };

  const onAuthRoutes = (
    <Routes>
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/dashboard" element={
        <ProtectedRoute onAuthChange={handleAuthChange}>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/" element={
        <ProtectedRoute onAuthChange={handleAuthChange}>
          <FolderUploader />
        </ProtectedRoute>
      } />
      <Route path="/extract" element={
        <ProtectedRoute onAuthChange={handleAuthChange}>
          <DataExtractor />
        </ProtectedRoute>
      } />
      <Route path="/results" element={
        <ProtectedRoute onAuthChange={handleAuthChange}>
          <ExtractedData />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );

  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<AuthSectionThree />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef2f4]">
      <Sidebar onAuthChange={handleAuthChange} />
      <div className="ml-60">
        <main className="mx-auto max-w-6xl px-6 py-8">{onAuthRoutes}</main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}
